import { FiMessageCircle } from 'react-icons/fi';

export const everydayConversationsData = {
  courseId: 'everyday-conversations',
  courseType: 'listening-course',
  title: 'Everyday Conversations',
  icon: FiMessageCircle,
  overview: {
    impact: 'Confidently follow social dialogues and extract key details from booking, transaction, and information-exchange scenarios',
    focus: 'Section 1 format: two speakers in a social/transactional conversation — bookings, purchases, appointments, and personal-information exchange',
    uses: 'IELTS Listening Section 1 (and some Section 2 dialogues)',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'Section 1 Overview',
      content: `<p><strong>Section 1</strong> is always a conversation between <strong>two people</strong> in an everyday social context. One speaker typically requests a service or information, while the other provides it. Common scenarios include booking a hotel room, making a restaurant reservation, registering for a course, renting equipment, or calling a doctor's surgery.</p>
<p>The questions usually ask for <strong>factual details</strong>: names, dates, times, prices, addresses, and reference numbers. Because the language is relatively straightforward, this section rewards careful attention to detail rather than advanced vocabulary.</p>`,
      points: [
        'Section 1 is worth 10 marks — the easiest section to score highly on.',
        'The audio is played ONCE. You have 30 seconds to preview questions before it starts.',
        'Answers are typically short: a word, a number, or a short phrase.',
        'Distractors appear often — a speaker may mention a wrong option before correcting it.',
        'Use the preview time to underline key words in questions and predict answer types.',
      ],
      warning: 'Even though Section 1 is the easiest, careless errors on numbers, spelling, and dates are extremely common. Treat every answer as important.',
    },
    {
      title: 'Common Situations and Vocabulary',
      content: `<p>Certain <strong>scenarios</strong> recur across IELTS Listening Section 1. Familiarising yourself with their typical vocabulary and question patterns gives you a significant advantage.</p>`,
      points: [
        'Accommodation: hotel, hostel, guest house, check-in/check-out, single/double room, en suite, bed and breakfast.',
        'Travel: departure, arrival, platform, terminal, one-way/return, direct flight, layover, boarding pass.',
        'Health: appointment, surgery, GP, prescription, symptoms, referral, waiting list.',
        'Courses and classes: enrolment, fee, timetable, prerequisite, certificate, assessment.',
        'Shopping and services: receipt, refund, exchange, warranty, delivery, installation.',
        'Banking and finance: account number, sort code, balance, transfer, standing order.',
      ],
      examples: [
        { label: 'Booking', text: '"I\'d like to book a double room for three nights, checking in on the tenth of June." → Room: double; Nights: 3; Date: 10 June' },
        { label: 'Transaction', text: '"The total comes to forty-seven pounds twenty. Would you like to pay by card?" → Price: £47.20' },
      ],
    },
    {
      title: 'Functional Language Patterns',
      content: `<p>Everyday conversations rely on <strong>functional language</strong> — fixed phrases used for specific purposes such as requesting, offering, suggesting, or confirming. Recognising these patterns helps you anticipate what information will follow.</p>`,
      points: [
        'Requests often use "Could you…," "Would it be possible to…," or "I was wondering if…"',
        'Offers use "Shall I…," "Would you like me to…," or "I can… if you\'d prefer."',
        'Suggestions use "How about…," "Why don\'t we…," "What about…"',
        'Confirmation: "So that\'s…," "Let me just check…," "To confirm…"',
        'Speakers frequently rephrase or summarise — these repetitions help you verify your answer.',
      ],
      table: {
        headers: ['Function', 'Common Phrases', 'What to Listen For'],
        rows: [
          ['Requesting', '"Could I have…" / "I\'d like to…" / "Can you tell me…"', 'The specific item, service, or information being asked for'],
          ['Offering', '"Shall I…" / "I can arrange…" / "Would you like…"', 'The option or service being proposed — and whether it is accepted'],
          ['Suggesting', '"How about…" / "Why don\'t we…" / "You could try…"', 'The alternative being proposed and the other speaker\'s response'],
          ['Confirming', '"So that\'s…" / "Let me read that back…" / "Just to confirm…"', 'The final, confirmed version of the information — this is your answer'],
          ['Correcting', '"Actually, I meant…" / "Sorry, it should be…" / "No, not X — Y."', 'The corrected detail — always write the correction, not the first version'],
        ],
      },
    },
    {
      title: 'Following the Conversation Flow',
      content: `<p>IELTS conversations follow a predictable <strong>structure</strong>: greeting → stating purpose → exchanging details → confirming/summarising → closing. Understanding this flow helps you predict when key information will appear.</p>
<p>Speakers also use <strong>discourse markers</strong> to signal transitions: "Right, so…," "Now, moving on to…," "The other thing is…," "Oh, and one more thing…" These cue you to listen for the next piece of information.</p>`,
      points: [
        'The first 10–15 seconds are usually a greeting and context-setting — use this time to orient yourself.',
        'Key details (names, numbers, dates) usually come in the middle of the conversation.',
        'Speakers may give an initial answer, then change it — always use the FINAL answer.',
        'Transition markers ("Right," "OK," "Now") signal that a new question topic is coming.',
        'The end of the conversation often includes a summary or confirmation of details.',
      ],
      examples: [
        { label: 'Correction flow', text: '"The appointment is at 3 o\'clock — actually, let me check — yes, 3:30." → Answer: 3:30' },
        { label: 'Transition', text: '"OK, and the last thing I need is your email address…" → Next answer will be an email.' },
      ],
    },
  ],
  exercises: [
    {
      title: 'Identify the Situation',
      type: 'select',
      instructions: 'Read each snippet of dialogue and identify the most likely situation.',
      questions: [
        {
          text: '"I\'d like a table for two at 7:30 this Friday, please. Do you have anything available near the window?"',
          options: ['Hotel check-in', 'Restaurant reservation', 'Doctor\'s appointment', 'Course enrolment'],
          answer: 'Restaurant reservation',
          explanation: '"Table for two," a specific time, and "near the window" are restaurant booking language.',
        },
        {
          text: '"I\'m calling about the leak in the bathroom. It started yesterday and the ceiling is getting damp."',
          options: ['Reporting a maintenance issue', 'Booking a plumber directly', 'Calling an insurance company', 'Complaining to a neighbour'],
          answer: 'Reporting a maintenance issue',
          explanation: 'The speaker is reporting a problem (leak) to a landlord or housing office — typical Section 1 scenario.',
        },
        {
          text: '"Could I register for the beginners\' pottery class? I saw it in the brochure. Is there still space?"',
          options: ['Gym membership', 'Course enrolment', 'Museum visit', 'Library card application'],
          answer: 'Course enrolment',
          explanation: '"Register," "class," and "brochure" signal a course or class enrolment situation.',
        },
        {
          text: '"I bought this jacket last week but the zip is already broken. I\'d like a refund, please."',
          options: ['Returning a faulty item', 'Booking a tailor', 'Ordering new clothes online', 'Dry cleaning request'],
          answer: 'Returning a faulty item',
          explanation: '"Bought last week," "broken," and "refund" clearly indicate a product return.',
        },
      ],
    },
    {
      title: 'Complete a Booking Conversation',
      type: 'fill-in',
      instructions: 'Fill in the blanks to complete each booking exchange with the most logical detail.',
      questions: [
        {
          text: '"I\'d like to book a single room for two nights. Checking in on ___." (The speaker says "the ninth of October.")',
          answer: '9 October',
          explanation: '"The ninth of October" = 9 October. You should write the date, not a day of the week.',
        },
        {
          text: '"And your surname?" "It\'s Petersen — that\'s P-E-T-E-R-S-E-N." Write the name: ___',
          answer: 'Petersen',
          explanation: 'The speaker spells it out: P-E-T-E-R-S-E-N. Note the "-en" ending, not "-on."',
        },
        {
          text: '"The total including breakfast is ___ per night." (The speaker says "sixty-five pounds.")',
          answer: '£65',
          explanation: '"Sixty-five pounds" = £65. Write the currency symbol for clarity.',
        },
        {
          text: '"We\'ll send confirmation to your email: ___." (The speaker says "j dot petersen at mail dot com.")',
          answer: 'j.petersen@mail.com',
          explanation: '"Dot" = period, "at" = @. Email addresses are spelled out carefully in IELTS.',
        },
      ],
    },
    {
      title: 'Match Functions to Phrases',
      type: 'select',
      instructions: 'Identify the communicative function of each phrase.',
      questions: [
        {
          text: '"Would you like me to send you a brochure in the post?"',
          options: ['Request', 'Offer', 'Suggestion', 'Correction'],
          answer: 'Offer',
          explanation: '"Would you like me to…" is a classic offer structure — the speaker is proposing to do something for the listener.',
        },
        {
          text: '"Why don\'t we try the Italian place on Park Street instead?"',
          options: ['Request', 'Offer', 'Suggestion', 'Confirmation'],
          answer: 'Suggestion',
          explanation: '"Why don\'t we…" proposes an alternative — a suggestion.',
        },
        {
          text: '"So that\'s a double room, checking in June tenth, three nights. Is that all correct?"',
          options: ['Request', 'Offer', 'Suggestion', 'Confirmation'],
          answer: 'Confirmation',
          explanation: '"So that\'s… Is that all correct?" summarises and seeks verification — a confirmation.',
        },
        {
          text: '"Could you spell your surname for me, please?"',
          options: ['Request', 'Offer', 'Suggestion', 'Correction'],
          answer: 'Request',
          explanation: '"Could you… please" is a polite request for information.',
        },
      ],
    },
    {
      title: 'Predict What Comes Next',
      type: 'select',
      instructions: 'Based on the conversation context, predict what type of information will come next.',
      questions: [
        {
          text: '"OK, and now I just need to take a few personal details. First of all, could you give me your…"',
          options: ['Credit card number', 'Full name', 'Opinion about the service', 'Travel itinerary'],
          answer: 'Full name',
          explanation: '"Personal details" and "first of all" suggest basic information — name is always asked first.',
        },
        {
          text: '"Right, the next available appointment is on Thursday. What time works best for you — morning or…"',
          options: ['A specific morning time', 'Afternoon', 'The name of the doctor', 'The address of the clinic'],
          answer: 'Afternoon',
          explanation: 'The speaker is offering a choice: "morning or…" will be completed with "afternoon."',
        },
        {
          text: '"The total comes to thirty-eight pounds. How would you like to…"',
          options: ['Pay', 'Have it delivered', 'Receive a discount', 'Change the order'],
          answer: 'Pay',
          explanation: 'After stating a total, the next step is asking about the payment method.',
        },
      ],
    },
    {
      title: 'Summarise a Conversation',
      type: 'writing',
      instructions: 'Read the conversation excerpt and write a brief summary capturing all key factual details.',
      questions: [
        {
          text: '"Hello, I\'m ringing to book a place on the weekend photography workshop. My name\'s Sarah Mitchell — that\'s M-I-T-C-H-E-L-L. I\'d prefer the Saturday session if there\'s still room. And could you tell me the fee?" "Certainly. The Saturday workshop runs from ten a.m. to four p.m. The fee is eighty-five pounds, which includes materials."',
          example: 'Name: Sarah Mitchell; Workshop: Photography (Saturday); Time: 10 a.m. – 4 p.m.; Fee: £85 (materials included)',
          tip: 'Extract every factual detail: name (check spelling), day, times, and price. Note what is included in the fee.',
        },
        {
          text: '"I need to arrange a home delivery for a washing machine I bought today. My address is 8 Birchwood Close — that\'s B-I-R-C-H-W-O-O-D — postcode LS6 3NP. Could it come on Tuesday?" "Tuesday\'s fully booked, I\'m afraid. The earliest would be Thursday the twenty-third." "That\'s fine."',
          example: 'Item: washing machine; Address: 8 Birchwood Close, LS6 3NP; Delivery: Thursday 23rd',
          tip: 'Notice the initial preference (Tuesday) was rejected. The confirmed date is Thursday the 23rd — always record the final agreed detail.',
        },
      ],
    },
  ],
  tips: [
    'Use the 30-second preview time to underline keywords and predict what type of answer each question needs (name, number, place, etc.).',
    'Listen for discourse markers like "Right," "OK so," "Now then" — they signal that a new topic (and likely a new question) is coming.',
    'If a speaker changes their mind, always write the corrected/final answer.',
    'Don\'t panic if you miss one answer — move on immediately or you risk missing the next two.',
    'Practise with a variety of accents: IELTS uses British, Australian, North American, and other English accents.',
    'Write short answers — one to three words or a number. Don\'t waste time writing full sentences.',
  ],
  dailyPractice: [
    'Listen to one English podcast or radio segment (5–10 min) and note every factual detail: names, numbers, places, times.',
    'Role-play a booking scenario with a partner — one person "calls" to book, the other takes the details.',
    'Watch a short scene from a British TV show and write down the key information exchanged in the dialogue.',
    'Practise filling out a form from dictation: have someone read details aloud while you complete the form.',
  ],
  backPath: '/exams/english/#listening',
  backLabel: 'Back to Listening',
  aiContext: 'Everyday Conversations: A beginner-level IELTS Listening skill course covering Section 1 social/transactional dialogues between two speakers. Topics include booking situations (hotel, restaurant, appointment), transactions (buying, renting, returning), personal information exchange, functional language (requests, offers, suggestions, confirmations, corrections), and conversation flow with discourse markers. Includes 17 practice questions across select, fill-in, and writing exercise types.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...everydayConversationsData,
      title: everydayConversationsData.title + ' (Foundations)',
      overview: { ...everydayConversationsData.overview, duration: '15-20 minutes' },
      sections: everydayConversationsData.sections.slice(0, Math.max(2, Math.ceil(everydayConversationsData.sections.length / 2))),
      exercises: everydayConversationsData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...everydayConversationsData,
      title: everydayConversationsData.title + ' (Advanced)',
      overview: { ...everydayConversationsData.overview, duration: '30-35 minutes' },
      sections: everydayConversationsData.sections,
      exercises: everydayConversationsData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(everydayConversationsData.tips || []),
        'For Band 8+: Train yourself to understand speakers at fast natural speed.',
        'Advanced tip: Practice with varied accents and complex academic content.',
      ],
    };
  }
  return everydayConversationsData;
}
